import { Fade } from "react-awesome-reveal";
import { motion, useMotionValue, useAnimationFrame } from "framer-motion";
import { useScrollVariables } from "../scrollVariables";
import { useRef, useEffect } from "react";

export default function Trooh() {
  const {
    labelContainerRef,
    projectTextRef,
    labelOpacity,
    projectTextOpacity,
  } = useScrollVariables();

  const carouselRef = useRef(null);
  const centeredIndex = useRef(0);

  const images = [
    "img/trooh-work/trooh-brandbook-1.jpg",
    "img/trooh-work/trooh-brandbook-2.jpg",
    "img/trooh-work/trooh-brandbook-3.jpg",
    "img/trooh-work/trooh-brandbook-4.jpg",
    "img/trooh-work/trooh-brandbook-5.jpg",
    "img/trooh-work/trooh-brandbook-6.jpg",
    "img/trooh-work/trooh-brandbook-7.jpg",
    "img/trooh-work/trooh-brandbook-8.jpg",
    "img/trooh-work/trooh-brandbook-9.jpg",
    "img/trooh-work/trooh-brandbook-10.jpg",
    "img/trooh-work/trooh-brandbook-11.jpg",
    "img/trooh-work/trooh-brandbook-12.jpg",
    "img/trooh-work/trooh-brandbook-13.jpg",
    "img/trooh-work/trooh-brandbook-14.jpg",
    "img/trooh-work/trooh-brandbook-15.jpg",
    "img/trooh-work/trooh-brandbook-16.jpg",
    "img/trooh-work/trooh-brandbook-17.jpg",
    "img/trooh-work/trooh-brandbook-18.jpg",
    "img/trooh-work/trooh-brandbook-19.jpg",
    "img/trooh-work/trooh-brandbook-20.jpg",
    "img/trooh-work/trooh-brandbook-22.jpg",
    "img/trooh-work/trooh-brandbook-23.jpg",
    "img/trooh-work/trooh-brandbook-24.jpg",
    "img/trooh-work/trooh-brandbook-25.jpg",
    "img/trooh-work/trooh-brandbook-26.jpg",
    "img/trooh-work/trooh-brandbook-27.jpg",
    "img/trooh-work/trooh-brandbook-28.jpg",
    "img/trooh-work/trooh-brandbook-29.jpg",
  ];

  const loopImages = [...images, ...images];

  const scrollX = useMotionValue(0);
  const targetScrollX = useRef(0);
  const hasCenteredFirst = useRef(false); // enable loop only after initial center

  // --- helpers ---------------------------------------------------------------

  // Find the child element (there are 2 duplicates) for base index that is closest
  // to the current viewport center, then return the exact target scrollLeft
  // needed to center it.
  const computeTargetForIndex = (baseIndex) => {
    const carousel = carouselRef.current;
    if (!carousel) return 0;

    const currentScroll = scrollX.get();
    const viewportCenter = currentScroll + carousel.clientWidth / 2;

    // Collect candidates for this base index across the doubled list
    const candidates = Array.from(carousel.children).filter(
      (el) => Number(el.dataset.baseIndex) === baseIndex
    );

    // Pick the duplicate whose center is closest to the viewport center
    let bestEl = null;
    let bestDist = Infinity;
    for (const el of candidates) {
      const elCenter = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(elCenter - viewportCenter);
      if (dist < bestDist) {
        bestDist = dist;
        bestEl = el;
      }
    }
    if (!bestEl) return currentScroll;

    // Exact center placement for that element
    const centerOffset = (carousel.clientWidth - bestEl.offsetWidth) / 2;
    return bestEl.offsetLeft - centerOffset;
  };

  // Center to the currently tracked index; snap allows instant set (for init/resize)
  const centerToCurrentIndex = (snap = false) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const nextTarget = computeTargetForIndex(centeredIndex.current);
    targetScrollX.current = nextTarget;

    if (snap) {
      scrollX.set(nextTarget);
      carousel.scrollLeft = nextTarget;
    }
  };

  // --- animation loop --------------------------------------------------------

  useAnimationFrame(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    // ease toward target
    const current = scrollX.get();
    const diff = targetScrollX.current - current;

    // If close enough, snap to exact target to kill any tiny nudge
    if (Math.abs(diff) <= 0.5) {
      scrollX.set(targetScrollX.current);
    } else {
      scrollX.set(current + diff * 0.15);
    }

    // Write to DOM
    carousel.scrollLeft = scrollX.get();

    // Handle seamless infinite loop only after initial center
    if (hasCenteredFirst.current) {
      const half = carousel.scrollWidth / 2;
      let s = scrollX.get();

      if (s >= half) {
        s -= half;
        scrollX.set(s);
        targetScrollX.current -= half;
        carousel.scrollLeft = s;
      } else if (s < 0) {
        s += half;
        scrollX.set(s);
        targetScrollX.current += half;
        carousel.scrollLeft = s;
      }
    }
  });

  // --- lifecycle: initial center + keep centered on resize -------------------

  useEffect(() => {
    // On mount: center first image and enable loop afterwards
    centerToCurrentIndex(true);
    hasCenteredFirst.current = true;

    // On resize: keep the same image perfectly centered (snap, no slide)
    const handleResize = () => centerToCurrentIndex(true);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- controls --------------------------------------------------------------

  const scroll = (direction) => {
    const count = images.length;
    centeredIndex.current =
      (centeredIndex.current + (direction === "left" ? -1 : 1) + count) % count;

    // Recompute exact target for that image (no fixed width assumptions)
    centerToCurrentIndex(false);
  };

  // --- UI --------------------------------------------------------------------

  return (
    <div className="trooh" id="trooh">
      <div className="label-container" ref={labelContainerRef}>
        <motion.div style={{ opacity: labelOpacity }}>01 TROOH MEDIA</motion.div>
      </div>

      <div className="container info-section justify-content-center">
        <div className="grid-even-columns">
          <div className="description-title">
            <h3>COMPANY</h3>
          </div>
          <div className="description-text">
            <h3>TROOH MEDIA</h3>
          </div>
        </div>
        <div className="grid-even-columns">
          <div className="description-title">
            <h3>ROLE</h3>
          </div>
          <div className="description-text">
            <h3>GRAPHIC DESIGNER</h3>
          </div>
        </div>
        <div className="grid-even-columns">
          <div className="description-title">
            <h3>DELIVERABLES</h3>
          </div>
          <div className="description-text">
            <h3>MOTION DESIGN</h3>
            <h3>BRAND & VISUAL IDENTITY</h3>
            <h3>BRAND STRATEGY</h3>
            <h3>SOCIAL MEDIA STRATEGY/CONTENT</h3>
          </div>
        </div>
        <div className="grid-even-columns">
          <div className="description-title">
            <h3>YEAR</h3>
          </div>
          <div className="description-text">
            <h3>2024 - PRESENT</h3>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-center">
        <motion.p
          className="project-text"
          ref={projectTextRef}
          style={{ opacity: projectTextOpacity }}
        >
          At Trooh, I was given the primary responsibility of creating and
          animating our own branded Trooh content, as well as contributing to
          the overall strategy and approach to make the content engaging for
          Gen-Z. Along with the motion design pieces, I contributed to the
          strategy and designed the pieces for our social media channels, and
          created the design for our brand identity guidelines.
        </motion.p>
      </div>

      <div className="container work-container">
        <div className="row align-items-center justify-content-center" id="gallery">
          <div className="col-12 col-lg-6 col-md-6 col-sm-12 text-center">
            <Fade duration={500}>
              <video
                playsInline
                autoPlay
                muted
                loop
                preload="metadata"
                src="img/trooh-work/trooh-screen.mp4"
                className="trooh-screen-mockup"
                alt="trooh-screen-mockup"
              />
            </Fade>
          </div>
          <div className="col-12 col-lg-6 col-md-6 col-sm-12 text-center">
            <Fade duration={500}>
              <video
                playsInline
                autoPlay
                muted
                loop
                preload="metadata"
                src="img/trooh-work/trooh-social.mp4"
                className="trooh-social-mockup"
                alt="trooh-social-mockup"
              />
            </Fade>
          </div>
        </div>
      </div>

      <Fade duration={500}>
        <div className="flex flex-col items-center mt-10">
          <div
            className="carousel flex overflow-x-hidden no-scrollbar w-[90%] py-4"
            ref={carouselRef}
          >
            {loopImages.map((src, i) => (
              <motion.img
                key={i}
                data-base-index={i % images.length}
                src={src}
                alt={`Trooh Carousel ${i + 1}`}
                className="flex-shrink-0 min-w-[200px] h-[150px] m-2 rounded-lg object-cover"
                draggable={false}
              />
            ))}
          </div>

          <div className="d-flex justify-content-center">
            <button
              className="scroll-button px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-lg"
              onClick={() => scroll("left")}
            >
              &lt;
            </button>
            <button
              className="scroll-button px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-lg"
              onClick={() => scroll("right")}
            >
              &gt;
            </button>
          </div>
        </div>
      </Fade>
    </div>
  );
}
